import React, { FC, useState, useRef, useEffect, useCallback,
  CSSProperties, KeyboardEventHandler, ChangeEvent, FocusEventHandler } from 'react';
import numeral from 'numeral';
import classNames from 'classnames';
import getSymbolFromCurrency from 'currency-symbol-map';
import { withStyles } from 'tss-react/mui';

import TextField, { TextFieldVariants } from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';

import QTypography from 'components/MUIWrappers/QTypography';
import QTip from 'components/QuickenControls/QTip';
import useQDialogs from 'components/QDialogs/useQDialogs';
import { noNaN, exists, isBrowser } from 'utils/utils';
import { quickenAmountToString } from 'data/transactions/utils';
import CalculatorIcon from 'assets/calculator.inline.svg';

import {
  ShowSignEnum, ShowColorEnum, internalFormatNumber, OPERATORS, applyOperator, round, PendingOperation, isWithinAmountLimit,
  MAX_AMOUNT, exceedsMaxAmount,
} from './utils';
import { amountFieldStyles, AmountFieldTheme } from './styles';

interface AmountFieldProps {
  editable?: boolean;
  className?: string;
  style?: CSSProperties;
  InputProps?: Record<string, any>;
  onKeyDown?: (event: KeyboardEventHandler<HTMLDivElement>) => void;
  zeroIsBlank?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, type?: string) => void;
  amountType?: string;
  value?: number;
  updateValue?: string;
  useAbsoluteValue?: boolean;
  onBlur?: (event: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>, type: string, enter: boolean) => void;
  onFocus?: (event: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  inputRef?: (element: HTMLInputElement | null) => void;
  currencySymbol?: string;
  currencyGap?: boolean;
  omitCents?: boolean;
  proposedSign?: number;
  lockInitialProposedSign?: boolean;
  showSign?: ShowSignEnum;
  showColor?: ShowColorEnum;
  color?: string;
  disableUnderline?: boolean;
  submitOnBlur?: boolean;
  onSubmit?: () => void;
  showAmountAdornment?: boolean;
  compact?: boolean;
  hideCurrencyString?: boolean;
  variant?: string;
  textFieldVariant?: TextFieldVariants;
  marginProp?: 'dense' | 'normal' | 'none';
  label?: string;
  maxChars?: number;
  classes?: AmountFieldTheme;
  id?: string;
  noWrap?: boolean;
  thinFont?: boolean;
  clickable?: boolean | string | null;
  fontSize?: string | number;
  enableCalculator?: boolean;
  calculatorPlaceholder?: string;
}

const AmountField: FC<AmountFieldProps> = ({
  editable,
  className,
  style,
  InputProps,
  onKeyDown,
  zeroIsBlank,
  onChange,
  amountType = 'amount',
  value,
  updateValue,
  useAbsoluteValue,
  onBlur,
  onFocus,
  autoFocus,
  inputRef,
  currencySymbol = 'USD',
  currencyGap,
  omitCents,
  proposedSign,
  lockInitialProposedSign,
  showSign,
  showColor = ShowColorEnum.MANUAL,
  color,
  disableUnderline,
  submitOnBlur,
  onSubmit,
  showAmountAdornment,
  compact,
  hideCurrencyString,
  variant,
  textFieldVariant = 'standard',
  marginProp,
  label,
  maxChars,
  classes,
  thinFont,
  clickable,
  fontSize,
  enableCalculator = true,
  calculatorPlaceholder = 'Amount ',
  ...others
}) => {

  const textInput = useRef<HTMLInputElement | null>(null);
  const initialValueRef = useRef(value);
  const proposedSignRef = useRef(proposedSign);
  const selection = useRef({ start: false, end: false });
  const previousEditable = useRef(editable);
  // Tracks the in-progress calculator operation so operator changes re-render the tooltip.
  const [pendingOperation, setPendingOperation] = useState<PendingOperation | null>(null);

  const { dialogAlert } = useQDialogs();

  const validateField = useCallback((rawValue, skipCurrencyStrip = false) => {
    // only allow 2 decimals in amount field, and only 1 decimal point
    // need to give each field their own handler, and then have a default handler
    let filteredChars = '';
    let sanitizedValue = skipCurrencyStrip ? rawValue : rawValue.replace('$', '').trim();
    const legalChars = amountType === 'amount' || amountType === 'balance' ? '0123456789.-+' : '0123456789.';
    // strip out illegal characters (only numbers and decimals)
    for (let i = 0; i < sanitizedValue.length; i += 1) {
      filteredChars += legalChars.indexOf(sanitizedValue[i]) < 0 ? '' : sanitizedValue[i];
    }
    sanitizedValue = filteredChars;
    // This code will ensure there are only 2 digits to the right of the decimal
    if (filteredChars) {
      if (filteredChars.indexOf('.') >= 0 && filteredChars.length > 1) {
        if (filteredChars.length - filteredChars.indexOf('.') > 3 || rawValue.indexOf('.') !== rawValue.lastIndexOf('.')) {
          sanitizedValue = parseFloat(Number(filteredChars).toFixed(2)).toString();
        }
      }
    }
    return sanitizedValue;
  }, [amountType]);

  const valueFromProps = useCallback(() => {
    let val = numeral(value || updateValue || '');
    if (editable && val === 0) {
      val = '';
    } else {
      val = val.format(`${showSign === ShowSignEnum.ALWAYS ? '+-' : ''}0,00.00`);
      val = validateField(val, true);
    }
    return val;
  }, [value, updateValue, editable, showSign, validateField]);

  const [valueState, setValueState] = useState(() => valueFromProps());

  const [currencyString, setCurrencyString] = useState(() => !hideCurrencyString ? (getSymbolFromCurrency(currencySymbol) || '').trim() : '');

  // Tracks real DOM focus so the calculator icon only shows while the field is actively being typed in.
  const [isFocused, setIsFocused] = useState(false);

  const formatOperand = useCallback((amount: number) => validateField(String(round(amount)), true), [validateField]);

  // Restores operandA as originally typed when an operator was pressed but abandoned with no second operand.
  const formatSignedOperand = useCallback((magnitude: number, explicitSign?: '+' | '-') => {
    if (explicitSign) {
      return `${explicitSign}${formatOperand(Math.abs(magnitude))}`;
    }
    return formatOperand(magnitude);
  }, [formatOperand]);

  // Formats a computed arithmetic result, adding an explicit '+' only when the expression started with a typed sign.
  const formatComputedResult = useCallback((magnitude: number, explicitSign?: '+' | '-') => {
    const formatted = formatOperand(magnitude);
    if (!explicitSign) {
      return formatted;
    }
    return magnitude >= 0 ? `+${formatted}` : formatted;
  }, [formatOperand]);

  // Formats an amount for the tooltip's calculator tape, adding a '+' only when explicitly typed.
  const formatTapeAmount = (magnitude: number, explicitSign?: '+' | '-') => {
    const formatted = numeral(magnitude).format('0,0.00');
    return explicitSign === '+' && magnitude >= 0 ? `+${formatted}` : formatted;
  };

  // Applies a calculator-driven value update and positions the cursor at the end of the new display value.
  const setCalculatedDisplayValue = (newVal: string, cursorOverride?: number) => {
    setValueState(newVal);
    let prefixLength = 0;
    if (!showAmountAdornment && currencyString) {
      prefixLength = currencyString.length + 1;
    }
    const cursorPosition = cursorOverride ?? (newVal === '' ? 0 : prefixLength + newVal.length);
    // @ts-expect-error: selection uses false as boolean sentinel; runtime values are number | null
    selection.current = { start: cursorPosition, end: cursorPosition };
  };

  // Forwards a calculator-driven value change to the parent's onChange (negating for useAbsoluteValue).
  const emitChangeValue = (event, nextValue) => {
    let outgoingValue = nextValue;
    if (useAbsoluteValue) {
      outgoingValue = `-${outgoingValue}`;
    }
    // eslint-disable-next-line no-param-reassign
    event.target.value = outgoingValue;
    if (onChange) {
      onChange(event, amountType);
    }
  };

  // Field resets only once the user clicks OK, not the moment the overflow is detected.
  const showAmountLimitDialog = () => {
    dialogAlert(
      'Amount exceeds limit',
      `Enter an amount up to ${MAX_AMOUNT}`,
      () => {
        setPendingOperation(null);
        setCalculatedDisplayValue('');
        emitChangeValue({ target: { value: '' } }, '');
      },
      ['OK'],
      'warning',
      false,
    );
  };

  // Resolves any pending calculator operation into a single committed number, on Enter or blur.
  const resolvePendingCalculation = () => {
    if (!pendingOperation) {
      return valueState;
    }
    setPendingOperation(null);
    if (pendingOperation.operandBDigits === '') {
      const resolvedValue = formatSignedOperand(pendingOperation.operandA, pendingOperation.explicitSign);
      setCalculatedDisplayValue(resolvedValue);
      return resolvedValue;
    }
    const calculatedTotal = applyOperator(pendingOperation.operandA, pendingOperation.operator, Number(pendingOperation.operandBDigits));
    if (exceedsMaxAmount(calculatedTotal)) {
      showAmountLimitDialog();
      return valueState;
    }
    const resolvedValue = isWithinAmountLimit(calculatedTotal)
      ? formatComputedResult(calculatedTotal, pendingOperation.explicitSign)
      : formatSignedOperand(pendingOperation.operandA, pendingOperation.explicitSign);
    setCalculatedDisplayValue(resolvedValue);
    return resolvedValue;
  };

  useEffect(() => {
    // Handle value updates
    if (!Number.isNaN(Number(value)) || !Number.isNaN(Number(updateValue))) {
      let newVal = value || updateValue || '';
      newVal = validateField(String(newVal), true);
      if (Number(newVal) !== Number(valueState)) {
        setValueState(newVal);
      }
    }

    // Handle proposedSign updates with lockInitialProposedSign logic
    if (
      proposedSign !== proposedSignRef.current &&
      (!lockInitialProposedSign || typeof proposedSignRef.current === 'undefined')
    ) {
      proposedSignRef.current = proposedSign;
    }

    // Handle currency symbol updates
    if (currencySymbol !== currencyString) {
      setCurrencyString((getSymbolFromCurrency(currencySymbol) || '').trim());
    }

    // Handle editable updates
    // This ensures that when the field becomes editable, it starts with the correct value
    if (editable && editable !== previousEditable.current) {
      setValueState(valueFromProps());
      previousEditable.current = editable;
      setPendingOperation(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, updateValue, currencySymbol, editable, proposedSign, lockInitialProposedSign, currencyString, validateField, valueFromProps]);

  useEffect(() => {
    if (textInput.current) {
      const { selectionStart, selectionEnd } = textInput.current;
      // @ts-expect-error: selection uses false as boolean sentinel; runtime values are number | null
      const startChanged = selection.current.start !== false && selection.current.start !== selectionStart;
      // @ts-expect-error: selection uses false as boolean sentinel; runtime values are number | null
      const endChanged = selection.current.end !== false && selection.current.end !== selectionEnd;
      const update = startChanged || endChanged;

      if (update && !isBrowser('safari')) {
        // @ts-expect-error: selection.current is typed as boolean but holds number | null at runtime
        textInput.current.setSelectionRange(selection.current.start, selection.current.end);
      }
    }
  });

  const tooltip = () => {
    if (compact) {
      return internalFormatNumber(value, currencyString, '0,0.00', showSign);
    }
    return '';
  };

  const Wrap = compact ? QTip : React.Fragment;
  const wrapProps = compact ? { title: tooltip() } : {};

  let colorClass;
  switch (showColor) {
    case ShowColorEnum.POSITIVE_ONLY:
      colorClass = (value ?? 0) > 0 ? classes?.green : undefined;
      break;
    case ShowColorEnum.NEGATIVE_ONLY:
      colorClass = (value ?? 0) < 0 ? classes?.red : undefined;
      break;
    case ShowColorEnum.ALWAYS:
      if ((value ?? 0) < 0) {
        colorClass = classes?.red;
      } else if ((value ?? 0) > 0) {
        colorClass = classes?.green;
      }
      break;
    case ShowColorEnum.MANUAL:
      colorClass = color ? classes?.[color as keyof AmountFieldTheme] : undefined;
      break;
    default:
      assert(false, `unknown color mode '${showColor}'`);
  }

  const otherProps = {
    style,
    onChange,
    onBlur,
    ...others,
  };

  const renderStaticValue = () => {
    if (!exists(value) || (zeroIsBlank && noNaN(value) === 0)) {
      return <span>{'\u00A0'}</span>; // Danger, u00A0 may be removed from browser support.
    }
    return internalFormatNumber(
      value,
      `${currencyString}${(currencyString && currencyGap) ? ' ' : ''}`,
      `0,0${omitCents ? '' : '.00'}${compact ? 'a' : ''}`,
      showSign,
    );
  };

  const handleChange = (e) => {
    if (textInput.current) {
      selection.current = {
        start: textInput.current.selectionStart,
        end: textInput.current.selectionEnd,
      };
    }
    let newVal = validateField(e.target.value);
    setValueState(newVal);
    if (useAbsoluteValue) {
      // make negative
      newVal = `-${newVal}`;
    }
    e.target.value = newVal;
    if (onChange) {
      onChange(e, amountType);
    }
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    const numCharsToStrip = (currencyString ? currencyString.length + 1 : 0);

    const { target } = e;
    setTimeout(() => {
      if (showAmountAdornment) {
        target.select();
      } else {
        target.setSelectionRange(numCharsToStrip, 99);
      }
    }, 0);

    if (onFocus) {
      onFocus(e);
    }
    initialValueRef.current = valueState;
  };

  const handleOnBlur = (e, wasEnter = false) => {
    const hadPendingCalculation = Boolean(pendingOperation);
    const resolvedVal = resolvePendingCalculation();
    if (hadPendingCalculation) {
      e.target.value = `${currencyString}${currencyString ? ' ' : ''}${resolvedVal}`;
    }
    if (validateField(e.target.value).trim().length > 0) {
      let newVal = validateField(e.target.value);

      const valChanged = Number(newVal) !== Number(initialValueRef.current);
      const absValChanged = Math.abs(Number(newVal)) !== Math.abs(Number(initialValueRef.current));

      if (onBlur) {
        if (valChanged) {
          // @ts-expect-error: absValChanged && ... produces boolean | number; falsy coerces to sign 1
          newVal = quickenAmountToString(newVal, absValChanged && (proposedSignRef.current || Math.sign(initialValueRef.current)));
        }
        e.target.value = newVal;
        onBlur(e, amountType, wasEnter);
      }

      setValueState(newVal);
    } else if (e.target.value === '' && onBlur) {
      onBlur(e, amountType, wasEnter);
    }

    if (submitOnBlur && onSubmit) {
      onSubmit();
    }
  };

  // Clears focus state on real native blur, then delegates to handleOnBlur to commit the value.
  const handleTextFieldBlur = (e) => {
    setIsFocused(false);
    handleOnBlur(e);
  };

  const setInputRef = (el) => {
    textInput.current = el;
    if (inputRef) {
      inputRef(el);
    }
  };

  // Updates the second operand's raw digits as they're typed, displayed as-is until resolved.
  const applyPendingDigits = (event, currentOperation: PendingOperation, operandBDigits: string) => {
    setPendingOperation({ ...currentOperation, operandBDigits });
    setCalculatedDisplayValue(operandBDigits);
    emitChangeValue(event, operandBDigits);
  };

  const handleKeyPress = (event) => {
    const isOperatorEligible = enableCalculator && (amountType === 'amount' || amountType === 'balance');

    if ((event.key === 'Enter' || event.key === 'Escape') && pendingOperation) {
      // Resolves the pending expression to a plain number on the first Enter/Escape press,
      // deferring row-level commit/cancel handling to a second press once resolved.
      event.preventDefault();
      event.stopPropagation();
      const resolvedValue = resolvePendingCalculation();
      emitChangeValue(event, resolvedValue);
      return;
    }

    // When the whole field is selected, a +/- keystroke replaces the value as a new number's sign, not an operator.
    let selectedValuePrefixLength = 0;
    if (!showAmountAdornment && currencyString) {
      selectedValuePrefixLength = currencyString.length + 1;
    }
    const isEntireValueSelected = Boolean(
      valueState.length > 0
      && textInput.current
      && textInput.current.selectionStart === selectedValuePrefixLength
      && textInput.current.selectionEnd === selectedValuePrefixLength + valueState.length,
    );

    if (isOperatorEligible && pendingOperation && event.key === 'Backspace') {
      event.preventDefault();
      if (pendingOperation.operandBDigits === '') {
        // nothing typed for the second operand yet: cancel the pending operator entirely
        setPendingOperation(null);
        const restoredOperandA = formatSignedOperand(pendingOperation.operandA, pendingOperation.explicitSign);
        setCalculatedDisplayValue(restoredOperandA);
        emitChangeValue(event, restoredOperandA);
      } else {
        applyPendingDigits(event, pendingOperation, pendingOperation.operandBDigits.slice(0, -1));
      }
      if (onKeyDown) {
        onKeyDown(event);
      }
      return;
    }

    if (isOperatorEligible && pendingOperation && /^[0-9.]$/.test(event.key)) {
      event.preventDefault();
      // mirror the single-operand duplicate-decimal guard below: don't let a second '.' through
      if (event.key === '.' && pendingOperation.operandBDigits.indexOf('.') >= 0) {
        if (onKeyDown) {
          onKeyDown(event);
        }
        return;
      }
      const nextOperandBDigits = validateField(pendingOperation.operandBDigits + event.key);
      applyPendingDigits(event, pendingOperation, nextOperandBDigits);
      if (onKeyDown) {
        onKeyDown(event);
      }
      return;
    }

    if (isOperatorEligible && event.key === '%') {
      // '%' converts whichever number is currently being entered into hundredths (500 -> 5.00)
      if (pendingOperation && pendingOperation.operandBDigits !== '') {
        event.preventDefault();
        applyPendingDigits(event, pendingOperation, formatOperand(Number(pendingOperation.operandBDigits) / 100));
        if (onKeyDown) {
          onKeyDown(event);
        }
        return;
      }
      if (!pendingOperation && !isEntireValueSelected && /\d/.test(valueState)) {
        event.preventDefault();
        const percentValue = formatOperand(Number(valueState) / 100);
        setCalculatedDisplayValue(percentValue);
        emitChangeValue(event, percentValue);
        if (onKeyDown) {
          onKeyDown(event);
        }
        return;
      }
    }

    if (isOperatorEligible && OPERATORS.includes(event.key)) {
      const hasDigits = /\d/.test(valueState) && !isEntireValueSelected;
      const isCursorAtStart = Boolean(
        textInput.current && textInput.current.selectionStart === selectedValuePrefixLength,
      );

      if (pendingOperation) {
        event.preventDefault();
        let { operandA } = pendingOperation;
        if (pendingOperation.operandBDigits !== '') {
          // Collapses the running total before starting the next operation.
          const collapsedTotal = applyOperator(pendingOperation.operandA, pendingOperation.operator, Number(pendingOperation.operandBDigits));
          if (exceedsMaxAmount(collapsedTotal)) {
            showAmountLimitDialog();
            if (onKeyDown) {
              onKeyDown(event);
            }
            return;
          }
          if (isWithinAmountLimit(collapsedTotal)) {
            operandA = collapsedTotal;
          }
        }
        // Carries the originally-typed sign forward through the whole expression.
        setPendingOperation({ operandA, operator: event.key, operandBDigits: '', explicitSign: pendingOperation.explicitSign });
        setCalculatedDisplayValue('');
        emitChangeValue(event, '');
      } else if (hasDigits && isCursorAtStart && (event.key === '+' || event.key === '-')) {
        // Cursor at the start of a committed number: replaces the leading sign instead of starting an operator.
        event.preventDefault();
        const unsignedValue = valueState.replace(/^[+-]/, '');
        const newValue = validateField(`${event.key}${unsignedValue}`, true);
        setCalculatedDisplayValue(newValue, selectedValuePrefixLength + 1);
        emitChangeValue(event, newValue);
      } else if (hasDigits) {
        event.preventDefault();
        const leadingChar = valueState.charAt(0);
        const explicitSign = (leadingChar === '+' || leadingChar === '-') ? leadingChar : undefined;
        setPendingOperation({ operandA: Number(valueState), operator: event.key, operandBDigits: '', explicitSign });
        setCalculatedDisplayValue('');
        emitChangeValue(event, '');
      } else if (event.key !== '+' && event.key !== '-') {
        // '*' or '/' with nothing typed yet has no operand to act on.
        event.preventDefault();
      }

      if (onKeyDown) {
        onKeyDown(event);
      }
      return;
    }

    if (event.key === '.' && valueState.indexOf('.') >= 0) {
      // this is so that you can reset a selection with a period if a period is already selected
      const subStr = valueState.substring(textInput.current?.selectionStart, textInput.current?.selectionEnd);
      const hasPeriod = subStr.indexOf('.') >= 0;
      if (!hasPeriod) {
        event.stopPropagation();
        event.preventDefault();
      }
    }
    if (event.key === 'Enter') {
      // if user hits Enter, treat like TAB and treat as a blur to capture the value
      const e = { target: { value: `${currencyString} ${valueState}` } };
      handleOnBlur(e, true);

      if (onSubmit) {
        onSubmit();
      }
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  if (editable) {
    // Shows a running calculator tape (operand, operator, in-progress second operand) while an operation is pending.
    const showCalculatorPlaceholder = Boolean(pendingOperation && pendingOperation.operandBDigits === '');
    const calculatorTapeContent = pendingOperation ? (
      <div className={classes?.calcTooltipContent}>
        <div className={classes?.calcTooltipOperatorColumn} />
        <div className={classes?.calcTooltipNumberColumn}>
          <div className={classes?.calcTooltipOperandRow}>{formatTapeAmount(pendingOperation.operandA, pendingOperation.explicitSign)}</div>
          <span className={classes?.calcTooltipOperandValue}>
            {pendingOperation.operandBDigits ? numeral(pendingOperation.operandBDigits).format('0,0.00') : ''}
          </span>
        </div>
        <div className={classes?.calcTooltipOperatorRow}>
          <span className={classes?.calcTooltipOperatorBadge}>{pendingOperation.operator}</span>
        </div>
      </div>
    ) : '';

    return (
      <Tooltip
        open={Boolean(pendingOperation)}
        title={calculatorTapeContent}
        placement="top-end"
        disableHoverListener
        disableFocusListener
        disableTouchListener
        classes={{ tooltip: classes?.calcTooltip }}
      >
        <TextField
          {...otherProps}
          sx={fontSize ? { fontSize } : undefined}
          className={classNames(classes?.inputField, className)}
          // @ts-expect-error sharedcomponentid is a custom prop not in MUI TextField types
          sharedcomponentid="AMOUNT_FIELD"
          value={showCalculatorPlaceholder ? '' : `${showAmountAdornment ? '' : `${currencyString}${currencyString ? ' ' : ''}`}${valueState}`}
          placeholder={showCalculatorPlaceholder ? calculatorPlaceholder : undefined}
          onChange={handleChange}
          autoFocus={autoFocus}
          label={label}
          margin={marginProp}
          onFocus={handleFocus}
          onBlur={handleTextFieldBlur}
          inputRef={setInputRef}
          onKeyDown={handleKeyPress}
          variant={textFieldVariant}
          InputProps={{
            ...InputProps,
            ...(disableUnderline && { disableUnderline: true }),
            inputProps: {
              className: classNames(classes?.inputField, showCalculatorPlaceholder && classes?.calcPlaceholderInput),
              maxLength: maxChars || (13 + (currencyString ? currencyString.length : 0) + (showSign === ShowSignEnum.NEVER ? 0 : 1)),
            },
            startAdornment: ((enableCalculator && isFocused) || showAmountAdornment) ? (
              <>
                {enableCalculator && isFocused && (
                  <InputAdornment position="start">
                    <CalculatorIcon className={classes?.calculatorAdornmentIcon} />
                  </InputAdornment>
                )}
                {showAmountAdornment && (
                  <InputAdornment classes={{ root: classes?.amountadornment }} disableTypography position="end">
                    {currencyString}
                  </InputAdornment>
                )}
              </>
            ) : (
              ''
            ),
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Wrap
      {...wrapProps}
    >
      <QTypography
        {...otherProps}
        thinFont={thinFont}
        clickable={clickable}
        sx={fontSize ? { fontSize } : undefined}
        variant={variant || 'inherit'}
        component="span"
        sharedcomponentid="AMOUNT_FIELD"
        className={classNames(...[classes?.inputField, className, ...(colorClass ? [colorClass] : [])])}
      >
        {renderStaticValue()}
      </QTypography>
    </Wrap>
  );
};

// @ts-expect-error amountFieldStyles returns plain style objects not CSSObject, incompatible with tss-react withStyles signature
export default withStyles(AmountField, amountFieldStyles);
